import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import {
  CopyTaskTrackerTaskRequest,
  CreateTaskTrackerTaskRequest,
  GetTaskTrackerColumnItemsParams,
  MoveTaskTrackerTaskRequest,
  TaskTrackerBoardCard,
  TaskTrackerColumnItemsPage,
  TaskTrackerTaskHistory,
  TransferTaskTrackerTaskRequest,
  UpdateTaskTrackerTaskRequest,
} from '../store/taskTracker.types'
import {
  mapBoardCardFromApi,
  mapBoardCardsFromApi,
  mapTaskHistoryFromApi,
} from './taskTracker.api.mappers'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import {
  ApiBoardCard,
  ApiDeleteTaskResponse,
  ApiTaskColumnItemsResponse,
  ApiTaskHistoryResponse,
} from './taskTracker.api.types'
import {
  DEFAULT_TASK_TRACKER_COLUMN_ITEMS_LIMIT,
  MAX_TASK_TRACKER_COLUMN_ITEMS_LIMIT,
} from '../lib/taskTrackerBoard.constants'
import { appendTaskTrackerStickerFiltersToSearchParams, appendTaskTrackerBoardCompletedStatusToSearchParams } from '../lib/taskTrackerBoard.stickerFilters.utils'

const buildColumnItemsQuery = ({
  boardId,
  columnId,
  cursor,
  limit,
  stickerFilters,
  isCompleted,
  sortBy,
  sortOrder,
}: GetTaskTrackerColumnItemsParams) => {
  const rawLimit = limit ?? DEFAULT_TASK_TRACKER_COLUMN_ITEMS_LIMIT
  const normalizedLimit = Math.min(
    Math.max(1, Math.floor(rawLimit)),
    MAX_TASK_TRACKER_COLUMN_ITEMS_LIMIT,
  )

  const searchParams = new URLSearchParams()
  searchParams.set('board_id', String(boardId))
  searchParams.set('column_id', String(columnId))
  searchParams.set('limit', String(normalizedLimit))

  const normalizedCursor = cursor?.trim()
  if (normalizedCursor) {
    searchParams.set('cursor', normalizedCursor)
  }

  const normalizedSortBy = sortBy?.trim()
  if (normalizedSortBy) {
    searchParams.set('sort_by', normalizedSortBy)
    searchParams.set('sort_order', sortOrder ?? 'asc')
  }

  appendTaskTrackerStickerFiltersToSearchParams(searchParams, stickerFilters)
  appendTaskTrackerBoardCompletedStatusToSearchParams(searchParams, isCompleted)

  return `?${searchParams.toString()}`
}

export const taskTrackerTasksApi = {
  async createTask(
    columnId: number,
    payload: CreateTaskTrackerTaskRequest,
  ): Promise<TaskTrackerBoardCard> {
    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      `task-tracker-tasks/create${getParams({ column_id: columnId })}`,
      {
        title: payload.title.trim(),
        ...(payload.performer_ids?.length
          ? { performer_ids: payload.performer_ids }
          : {}),
        ...(payload.field_values?.length
          ? { field_values: payload.field_values }
          : {}),
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось создать задачу'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось создать задачу')
    }
    return mapBoardCardFromApi(res.data.data)
  },

  async copyTask(taskId: number): Promise<TaskTrackerBoardCard> {
    const payload: CopyTaskTrackerTaskRequest = {
      task_id: taskId,
    }

    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      'task-tracker-tasks/copy',
      payload,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось скопировать задачу'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось скопировать задачу')
    }
    return mapBoardCardFromApi(res.data.data)
  },

  async transferTask(
    payload: TransferTaskTrackerTaskRequest,
  ): Promise<TaskTrackerBoardCard> {
    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      'task-tracker-tasks/transfer',
      payload,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось перенести задачу'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось перенести задачу')
    }
    return mapBoardCardFromApi(res.data.data)
  },

  async moveTask(
    payload: MoveTaskTrackerTaskRequest,
  ): Promise<TaskTrackerBoardCard> {
    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      'task-tracker-tasks/move',
      payload,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось переместить задачу'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось переместить задачу')
    }
    return mapBoardCardFromApi(res.data.data)
  },

  async updateTask(
    taskId: number,
    payload: UpdateTaskTrackerTaskRequest,
  ): Promise<TaskTrackerBoardCard> {
    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      `task-tracker-tasks/update${getParams({ task_id: taskId })}`,
      payload,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить задачу'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить задачу')
    }
    return mapBoardCardFromApi(res.data.data)
  },

  async updateTaskDescription(
    taskId: number,
    description: string | null,
    fileIds: number[] = [],
  ): Promise<TaskTrackerBoardCard> {
    const res = await API.post<ResponseWrapper<ApiBoardCard>>(
      `task-tracker-tasks/update-description${getParams({ task_id: taskId })}`,
      {
        description,
        ...(fileIds.length > 0 ? { file_ids: fileIds } : {}),
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить описание задачи'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить описание задачи')
    }
    return mapBoardCardFromApi(res.data.data)
  },

  async deleteTask(
    taskId: number,
  ): Promise<{ deleted: boolean; taskId: number; boardId: number }> {
    const res = await API.delete<ResponseWrapper<ApiDeleteTaskResponse>>(
      `task-tracker-tasks/delete${getParams({ task_id: taskId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось удалить задачу'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось удалить задачу')
    }

    return {
      deleted: Boolean(res.data.data.deleted),
      taskId: Number(res.data.data.task_id),
      boardId: Number(res.data.data.board_id),
    }
  },

  async getTask(taskId: number): Promise<TaskTrackerBoardCard> {
    const res = await API.get<ResponseWrapper<ApiBoardCard>>(
      `task-tracker-tasks/view${getParams({ task_id: taskId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось загрузить задачу'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось загрузить задачу')
    }

    return mapBoardCardFromApi(res.data.data)
  },

  async getColumnItems(
    params: GetTaskTrackerColumnItemsParams,
  ): Promise<TaskTrackerColumnItemsPage> {
    const res = await API.get<ResponseWrapper<ApiTaskColumnItemsResponse>>(
      `task-tracker-tasks/column-items${buildColumnItemsQuery(params)}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось догрузить задачи колонки'),
      )
    }

    const data = res.data?.data
    if (!data) {
      throw new Error('Не удалось догрузить задачи колонки')
    }

    return {
      boardId: Number(data.board_id),
      columnId: Number(data.column_id),
      items: mapBoardCardsFromApi(data.items),
      nextCursor: data.next_cursor ?? null,
      hasMore: Boolean(data.has_more),
      totalCount: Number(data.total_count ?? 0),
    }
  },

  async getTaskHistory(taskId: number): Promise<TaskTrackerTaskHistory> {
    const res = await API.get<ResponseWrapper<ApiTaskHistoryResponse>>(
      `task-tracker-tasks/history${getParams({ task_id: taskId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить историю задачи'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось загрузить историю задачи')
    }

    return mapTaskHistoryFromApi(res.data.data)
  },
}
